import { useEffect, useState, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Empty, Change } from "@/components/widgets";
import { ArrowClockwise, Brain } from "@phosphor-icons/react";
import { AssetLogo } from "@/components/AssetLogo";

const scoreColor = (s) => s >= 60 ? "text-signal-green" : s <= 40 ? "text-signal-red" : "text-signal-yellow";
const dirColor = (d) => d === "bullish" ? "text-signal-green" : d === "bearish" ? "text-signal-red" : "text-muted-foreground";

export default function BrokerAnalytics() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/alpaca/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const hist = (data?.equity_history || []).filter((p) => p.equity > 0);
  const positions = data?.positions || [];

  return (
    <div className="mt-4 border border-border bg-card" data-testid="broker-analytics">
      <div className="border-b border-border px-4 py-3 label-xs flex items-center justify-between">
        <span className="flex items-center gap-2"><Brain size={14} className="text-volt" /> Broker P/L · Blended with MarketPilot Intelligence</span>
        <button data-testid="broker-analytics-refresh" onClick={load} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-white transition-colors">
          <ArrowClockwise size={13} className={busy ? "animate-spin" : ""} /> refresh
        </button>
      </div>

      {/* Equity curve */}
      <div className="p-4 border-b border-border/50">
        <div className="label-xs mb-2">Account Equity · last month</div>
        {hist.length < 2 ? <Empty>Not enough equity history yet — it builds as your account trades.</Empty> : (
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hist} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#007AFF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8A93" }} tickLine={false} axisLine={false} minTickGap={40} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10, fill: "#8A8A93" }} tickLine={false} axisLine={false} width={60} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "#141415", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => [`$${Number(v).toLocaleString()}`, "Equity"]} />
                <Area type="monotone" dataKey="equity" stroke="#007AFF" strokeWidth={2} fill="url(#eqFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Positions × Intelligence */}
      <div className="label-xs px-4 py-2 border-b border-border/50">Positions × AI Signal</div>
      {positions.length === 0 ? <Empty>No broker positions yet — once an order fills, its live P/L and AI signal appear here.</Empty> : (
        <>
          <div className="grid grid-cols-[1fr_70px_100px_110px_70px_90px_1fr] gap-2 px-4 py-2 label-xs border-b border-border/50">
            <span>Symbol</span><span className="text-right">Qty</span><span className="text-right">Mkt Value</span><span className="text-right">Unreal. P/L</span><span className="text-right">AI</span><span className="text-right">Signal</span><span className="text-right">5d Outlook (model)</span>
          </div>
          {positions.map((p) => (
            <div key={p.ticker} data-testid={`analytics-row-${p.ticker}`} className="grid grid-cols-[1fr_70px_100px_110px_70px_90px_1fr] gap-2 items-center px-4 py-3 border-b border-border/50">
              <div className="mono font-medium flex items-center gap-2"><AssetLogo ticker={p.ticker} size={20} />{p.ticker}</div>
              <div className="mono text-right text-sm">{p.quantity}</div>
              <div className="mono text-right text-sm">${p.market_value.toLocaleString()}</div>
              <div className="text-right"><div className={`mono text-sm ${p.unrealized_pnl >= 0 ? "pos" : "neg"}`}>{p.unrealized_pnl >= 0 ? "+" : ""}${p.unrealized_pnl.toFixed(2)}</div><div className="mono text-[11px]"><Change value={p.unrealized_pnl_pct} /></div></div>
              <div className={`mono text-right text-sm font-semibold ${p.ai_score != null ? scoreColor(p.ai_score) : "text-muted-foreground"}`}>{p.ai_score ?? "—"}</div>
              <div className={`mono text-right text-xs uppercase ${dirColor(p.ai_direction)}`}>{p.ai_direction || "—"}</div>
              <div className="text-right">
                {p.forecast_5d ? (
                  <div className="flex items-center justify-end gap-2">
                    <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-white/10 border border-dashed border-white/20" title="model output">
                      <span className="bg-signal-green" style={{ width: `${(p.forecast_5d.prob_bull || 0) * 100}%` }} />
                      <span className="bg-signal-yellow/60" style={{ width: `${(p.forecast_5d.prob_neutral || 0) * 100}%` }} />
                      <span className="bg-signal-red" style={{ width: `${(p.forecast_5d.prob_bear || 0) * 100}%` }} />
                    </div>
                    <span className={`mono text-[11px] ${(p.forecast_5d.expected_return_pct || 0) >= 0 ? "pos" : "neg"}`}>{(p.forecast_5d.expected_return_pct || 0) >= 0 ? "+" : ""}{p.forecast_5d.expected_return_pct}%</span>
                  </div>
                ) : <span className="mono text-xs text-muted-foreground">—</span>}
              </div>
            </div>
          ))}
          <div className="px-4 py-2 mono text-[10px] text-muted-foreground italic border-dashed">Bars = 5-day probabilistic model output (green bull / yellow neutral / red bear) · not a prediction.</div>
        </>
      )}
    </div>
  );
}
